import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { bridgeApi } from '@/lib/bridgeApi';

interface HomeCareProvider {
  id: string;
  provider_name: string;
  provider_type: string;
  email: string;
  mobile: string;
  alt_contact: string;
  city: string;
  state: string;
  address: string;
  pincode: string;
  city_id?: string;
  state_id?: string;
  area_id?: string;
  area?: string;
  experience_years: number;
  hourly_rate: number;
  daily_rate: number;
  monthly_rate: number;
  services_offered: string[];
  availability_status: string;
  kyc_status: string;
  is_verified: boolean;
  is_active: boolean;
  subscription_plan: string;
  photo_url: string;
  aadhaar_verified: boolean;
  pan_verified: boolean;
  police_verified: boolean;
  adminSession?: boolean;
}

interface HomeCareAuthContextType {
  provider: HomeCareProvider | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdminSession: boolean;
  providerToken: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  register: (data: any) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  refreshProvider: () => Promise<void>;
}

const HomeCareAuthContext = createContext<HomeCareAuthContextType | undefined>(undefined);

const STORAGE_KEY = 'homecare_auth';
const TOKEN_KEY = 'homecare_auth_token';

export const HomeCareAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [provider, setProvider] = useState<HomeCareProvider | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdminSession, setIsAdminSession] = useState(false);
  const [providerToken, setProviderToken] = useState<string | null>(null);

  // Check if this is an admin impersonation session
  const checkAdminImpersonation = (providerId: string): boolean => {
    try {
      // Check for global admin session flag
      const adminSession = localStorage.getItem('adminSession');
      if (adminSession === 'true') return true;
      
      const raw = localStorage.getItem('patakare_impersonation');
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      
      // Accept if admin session with matching member type (4 = HomeCare+) or entity type
      return (
        parsed?.adminSession === true &&
        (parsed?.memberType === 4 || parsed?.entityType === 'homecare_provider')
      );
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const isAdminImpersonation = parsed.adminSession || checkAdminImpersonation(parsed.id);
        
        // For admin sessions, skip verification entirely
        if (isAdminImpersonation) {
          setProvider(parsed);
          setIsAdminSession(true);
          setIsLoading(false);
          return;
        }
        
        // For regular sessions, verify but with graceful fallback
        verifyProvider(parsed.id).then((result) => {
          if (result.shouldAccept) {
            setProvider(parsed);
            const storedToken = localStorage.getItem(TOKEN_KEY);
            if (storedToken) setProviderToken(storedToken);
          } else {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(TOKEN_KEY);
          }
          setIsLoading(false);
        });
      } catch (e) {
        localStorage.removeItem(STORAGE_KEY);
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  // Graceful verification - returns shouldAccept: true if API fails or doesn't exist
  const verifyProvider = async (id: string): Promise<{ shouldAccept: boolean; isActive?: boolean }> => {
    try {
      const result = await bridgeApi.call<any>('homecare_provider_verify', { id });
      // API responded successfully
      if (result?.is_active === true) {
        return { shouldAccept: true, isActive: true };
      }
      if (result?.is_active === false) {
        return { shouldAccept: false, isActive: false };
      }
      // Unknown response - accept session
      return { shouldAccept: true };
    } catch (error: any) {
      // API failed or doesn't exist - gracefully accept session
      console.warn('[HomeCareAuth] Verification skipped (API unavailable):', error.message);
      return { shouldAccept: true };
    }
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; message: string }> => {
    setIsLoading(true);
    
    try {
      const result = await bridgeApi.post<any>('homecare_login', { email, password });
      
      if (!result.success) {
        setIsLoading(false);
        return { success: false, message: result.message || 'Login failed' };
      }

      const prov = result.provider;

      // HomeCare+ allows access even in pending status (with notification)
      // Only reject if KYC is explicitly rejected or account is inactive
      if (prov.kyc_status === 'rejected') {
        setIsLoading(false);
        return { success: false, message: 'Your KYC was rejected. Please contact support.' };
      }

      if (prov.is_active === false || prov.is_active === 0) {
        setIsLoading(false);
        return { success: false, message: 'Your account is currently inactive. Please contact support.' };
      }

      const services = Array.isArray(prov.services_offered) 
        ? prov.services_offered 
        : (typeof prov.services_offered === 'string' ? (JSON.parse(prov.services_offered) || []) : []);

      const providerData: HomeCareProvider = {
        id: prov.id,
        provider_name: prov.provider_name || prov.full_name || '',
        provider_type: prov.provider_type || '',
        email: prov.email || '',
        mobile: prov.mobile || prov.contact_number || '',
        alt_contact: prov.alt_contact || '',
        city: prov.city || '',
        state: prov.state || '',
        address: prov.address || '',
        pincode: prov.pincode || '',
        experience_years: Number(prov.experience_years) || 0,
        hourly_rate: Number(prov.hourly_rate) || 0,
        daily_rate: Number(prov.daily_rate) || 0,
        monthly_rate: Number(prov.monthly_rate) || 0,
        services_offered: services,
        availability_status: prov.availability_status || 'available',
        kyc_status: prov.kyc_status || 'pending',
        is_verified: Boolean(prov.is_verified),
        is_active: Boolean(prov.is_active),
        subscription_plan: prov.subscription_plan || 'free',
        photo_url: prov.photo_url || '',
        aadhaar_verified: Boolean(prov.aadhaar_verified),
        pan_verified: Boolean(prov.pan_verified),
        police_verified: Boolean(prov.police_verified),
      };

      setProvider(providerData);
      setIsAdminSession(false);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(providerData));

      if (result.token) {
        setProviderToken(result.token);
        localStorage.setItem(TOKEN_KEY, result.token);
      }

      setIsLoading(false);

      return { success: true, message: 'Login successful!' };
    } catch (err: any) {
      setIsLoading(false);
      return { success: false, message: err.message || 'An error occurred. Please try again.' };
    }
  };

  const register = async (data: any): Promise<{ success: boolean; message: string }> => {
    try {
      const result = await bridgeApi.post<any>('homecare_register', data);
      if (result && result.success === false) {
        return { success: false, message: result.error || result.errors?.join(', ') || 'Registration failed' };
      }
      return { success: true, message: result?.message || 'Registration submitted' };
    } catch (err: any) {
      return { success: false, message: err.message || 'Registration failed' };
    }
  };

  const logout = () => {
    setProvider(null);
    setIsAdminSession(false);
    setProviderToken(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('patakare_impersonation');
  };

  const refreshProvider = async () => {
    if (!provider) return;

    try {
      const result = await bridgeApi.call<any>('homecare_provider_get', { id: provider.id, token: providerToken });
      // Handle both {data: {...}} and direct object
      const data = result?.data || result;
      let activePlan = data?.subscription_plan || 'free';

      try {
        const subResult = await bridgeApi.call<any>('homecare_subscription_get', { provider_id: provider.id });
        const subData = subResult?.data || subResult;
        if (subData?.status === 'active' && subData?.plan_name) {
          activePlan = subData.plan_name;
        }
      } catch {}

      if (data && (data.provider_name || data.business_name)) {
        const services = Array.isArray(data.services_offered) 
          ? data.services_offered 
          : (typeof data.services_offered === 'string' ? (JSON.parse(data.services_offered) || []) : []);
          
        const updated: HomeCareProvider = {
          id: data.id,
          provider_name: data.provider_name || data.business_name || data.full_name || '',
          provider_type: data.provider_type || '',
          email: data.email || '',
          mobile: data.mobile || data.contact_number || '',
          alt_contact: data.alt_contact || '',
          city: data.city || '',
          state: data.state || '',
          address: data.address || '',
          pincode: data.pincode || '',
          experience_years: Number(data.experience_years) || 0,
          hourly_rate: Number(data.hourly_rate) || 0,
          daily_rate: Number(data.daily_rate) || 0,
          monthly_rate: Number(data.monthly_rate) || 0,
          services_offered: services,
          availability_status: data.availability_status || 'available',
          kyc_status: data.kyc_status || 'pending',
          is_verified: Boolean(data.is_verified),
          is_active: Boolean(data.is_active),
          subscription_plan: activePlan,
          photo_url: data.photo_url || '',
          aadhaar_verified: Boolean(data.aadhaar_verified),
          pan_verified: Boolean(data.pan_verified),
          police_verified: Boolean(data.police_verified),
          adminSession: provider.adminSession,
        };
        setProvider(updated);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      }
    } catch (e) {
      console.error('Failed to refresh provider:', e);
    }
  };

  return (
    <HomeCareAuthContext.Provider
      value={{
        provider,
        isLoading,
        isAuthenticated: !!provider,
        isAdminSession,
        providerToken,
        login,
        register,
        logout,
        refreshProvider,
      }}
    >
      {children}
    </HomeCareAuthContext.Provider>
  );
};

export const useHomeCareAuth = () => {
  const context = useContext(HomeCareAuthContext);
  if (context === undefined) {
    throw new Error('useHomeCareAuth must be used within a HomeCareAuthProvider');
  }
  return context;
};
